import { useState, useEffect } from "react";
import { ActionIcon, AppShell, Box, Burger, MantineProvider, NavLink, Text, Group, ThemeIcon, ScrollArea, Button, Menu, Avatar, Badge, Tooltip } from "@mantine/core";
import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";
import { useDisclosure } from "@mantine/hooks";
import { Redirect, Route, Switch, Router, useLocation, useRoute } from "wouter";
import { ErrorBoundary } from "@sentry/react";
import ErrorFallback from "../../Boilerplate/_Shared/ErrorFallback";
import {
  IconUsers,
  IconBriefcase,
  IconSearch,
  IconFileText,
  IconHistory,
  IconReceipt2,
  IconX,
  IconBuilding,
  IconUser,
  IconLogout,
  IconKey,
  IconSettings,
  IconShieldLock,
  IconServerCog,
  IconBuildingFactory,
  IconUserStar,
  IconTag,
  IconMessageChatbot,
  IconCreditCard,
  IconDatabaseExport,
  IconListCheck,
  IconAdjustments,
  IconShieldSearch,
} from "@tabler/icons-react";

import { useTranslation } from "react-i18next";
import { NBoilerplateContext } from "../../Boilerplate/NBoilerplateContext";
import { userLogout, getTenantProfile } from "../../Boilerplate/api";
import { getEntitlements } from "./Pricing/pricingApi";
import { getFeedUnreadCount, getTaskCounts } from "./api";
import { usePlan } from "./hooks/usePlan";
import { requestUpgrade } from "./Pricing/upgrade";
import PlansPage from "./Pricing/PlansPage";
import CreditsPage from "./Pricing/CreditsPage";
import SpendReportPage from "./Pricing/SpendReportPage";
import UpgradeRequiredModal from "./components/UpgradeRequiredModal";
import UserInitialsAvatar from "./components/UserInitialsAvatar";
import { Wordmark } from "./_Brand/Wordmark";

import LoginForm from "../../Boilerplate/Auth/Login";
import RegisterForm from "../../Boilerplate/Auth/Register";
import ForgotPassword from "../../Boilerplate/Auth/ForgotPassword";
import ResetPassword from "../../Boilerplate/Auth/ResetPassword";
import Confirmation from "../../Boilerplate/Auth/Confirmation";
import ChangePassword from "../../Boilerplate/Auth/ChangePassword";
import SetPassword from "../../Boilerplate/Auth/SetPassword";
import LoginLinkHandler from "../../Boilerplate/Auth/LoginLink";
import InvitationForm from "../../Boilerplate/Auth/Invitation";
import ProfileEdit from "../../Boilerplate/Profile/ProfileEdit";

import UserList from "../../Boilerplate/Users/UserList";
import UserAdd from "../../Boilerplate/Users/UserAdd";
import UserEdit from "../../Boilerplate/Users/UserEdit";
import UserDetails from "../../Boilerplate/Users/UserDetails";
import TenantProfileEdit from "../../Boilerplate/Tenants/TenantProfileEdit";
import ForcedPasskeyEnrollment from "../../Boilerplate/Auth/ForcedPasskeyEnrollment";
import TenantList from "../../Boilerplate/AdminTenants/TenantList";
import TenantCreate from "../../Boilerplate/AdminTenants/TenantCreate";
import TenantEdit from "../../Boilerplate/AdminTenants/TenantEdit";
import TenantUserCreate from "../../Boilerplate/Tenants/TenantUserCreate";
import AdminUsersList from "../../Boilerplate/AdminUsers/AdminUsersList";
import AdminUserDetails from "../../Boilerplate/AdminUsers/AdminUserDetails";
import JobListAdmin from "../../Boilerplate/AdminJobs/Jobs";
import JobDetailsAdmin from "../../Boilerplate/AdminJobs/JobDetails";
import AdminDashboard from "./Admin/AdminDashboard";
import AdminTemplatesPage from "./Admin/AdminTemplatesPage";
import AdminTagsPage from "./Admin/AdminTagsPage";
import AdminApiKeysPage from "./Admin/AdminApiKeysPage";
import AdminCompaniesPage from "./Admin/AdminCompaniesPage";
import EnrichmentSettingsPage from "./Admin/EnrichmentSettingsPage";
import ConsentSettingsPage from "./Admin/ConsentSettingsPage";
import DataExportPage from "./Admin/DataExportPage";

import { shadcnCssVariableResolver } from "./cssVariableResolver";
import { shadcnTheme } from "./theme";
import "./theme.css";

import { store, useStore } from "./store";

import CandidateList from "./Candidates/CandidateList";
import CandidateProfile from "./Candidates/CandidateProfile";
import ClientList from "./Clients/ClientList";
import ClientDetail from "./Clients/ClientDetail";
import CompanyPage from "./Companies/CompanyPage";
import ProjectList from "./Projects/ProjectList";
import ProjectPipeline from "./Projects/ProjectPipeline";
import SearchPage from "./Search/SearchPage";
import DocumentsPage from "./Documents/DocumentsPage";
import ToolsPage from "./BackgroundCheck/ToolsPage";
import ActivityFeed from "./Activity/ActivityFeed";
import TasksPage from "./Tasks/TasksPage";
import NotificationBell from "./Notifications/NotificationBell";
import PreferencesPage from "./Settings/PreferencesPage";
import { resolveHomePath } from "./homePage";
import ImportPage from "./Candidates/ImportPage";
import DeletedCandidatesPage from "./Candidates/DeletedCandidatesPage";
import ChatDrawer from "./Assistant/ChatDrawer";
import ConsentReview from "./Consent/ConsentReview";
import LangSelector from "../../Boilerplate/Auth/LangSelector";

function AppNavbar({ toggle }) {
  const [location] = useLocation();
  const profile = useStore((state) => state.profile);
  const { t } = useTranslation("Veft");
  const { canUseCollaboration } = usePlan();
  const hideActivity = useStore((state) => state.hideActivity);
  const feedUnread = useStore((state) => state.feedUnread);
  const setFeedUnread = useStore((state) => state.setFeedUnread);
  const taskNavBadge = useStore((state) => state.taskNavBadge);
  const setTaskNavBadge = useStore((state) => state.setTaskNavBadge);

  // The activity feed shows when the tenant has collaboration AND the user hasn't opted out.
  const showActivity = canUseCollaboration && !hideActivity;

  // Refresh the unread-feed count on navigation (cheap count query), except while viewing the feed
  // itself — the feed page marks it seen and zeroes the badge. Skipped when activity is hidden
  // (no badge to show).
  useEffect(() => {
    if (!showActivity || location.startsWith("/activity")) return;
    getFeedUnreadCount().then((r) => setFeedUnread(r?.count ?? 0)).catch(() => {});
  }, [showActivity, location, setFeedUnread]);

  // Refresh the "due today / overdue" task badge on navigation, except on the Tasks page itself
  // (which keeps it current as the user works there).
  useEffect(() => {
    if (location.startsWith("/tasks")) return;
    getTaskCounts().then((c) => setTaskNavBadge(c?.navBadge ?? 0)).catch(() => {});
  }, [location, setTaskNavBadge]);

  const links = [
    // Tenant activity feed — the collaboration feature (Trial preview + Pro+). First item (it's the
    // landing page when available); hidden when the tenant lacks it or the user opted out; carries
    // an unread badge.
    ...(showActivity ? [{
      label: t("nav_activity"), icon: IconHistory, href: "/activity",
      rightSection: feedUnread > 0
        ? <Badge size="xs" variant="filled" color="blue">{feedUnread > 99 ? "99+" : feedUnread}</Badge>
        : undefined,
    }] : []),
    { label: t("nav_candidates"), icon: IconUsers, href: "/candidates" },
    {
      label: t("nav_tasks"), icon: IconListCheck, href: "/tasks",
      rightSection: taskNavBadge > 0
        ? <Badge size="xs" variant="filled" color="orange">{taskNavBadge > 99 ? "99+" : taskNavBadge}</Badge>
        : undefined,
    },
    { label: t("nav_projects"), icon: IconBriefcase, href: "/projects" },
    { label: t("nav_clients"), icon: IconBuilding, href: "/clients" },
    { label: t("nav_search"), icon: IconSearch, href: "/search" },
    { label: t("nav_documents"), icon: IconFileText, href: "/documents" },
    { label: t("nav_tools"), icon: IconShieldSearch, href: "/tools" },
  ];

  const tenantAdminLinks = [
    { label: t("nav_users"), icon: IconUsers, href: "/users" },
    { label: t("nav_tags"), icon: IconTag, href: "/settings/tags" },
    { label: t("nav_api_keys"), icon: IconKey, href: "/settings/api-keys" },
    { label: t("nav_enrichment"), icon: IconSettings, href: "/settings/enrichment" },
    { label: t("consent"), icon: IconShieldLock, href: "/settings/consent" },
    { label: t("export_nav"), icon: IconDatabaseExport, href: "/settings/export" },
    { label: t("plans_title"), icon: IconCreditCard, href: "/plans" },
    { label: t("nav_spend_report"), icon: IconReceipt2, href: "/settings/spend-report" },
    { label: t("nav_settings"), icon: IconSettings, href: "/settings/tenant" },
  ];

  const globalAdminLinks = [
    { label: t("nav_admin"), icon: IconShieldLock, href: "/admin" },
    { label: t("nav_tenants"), icon: IconBuildingFactory, href: "/tenants" },
    { label: t("nav_jobs"), icon: IconServerCog, href: "/admin/jobs" },
    { label: t("nav_admin_users"), icon: IconUserStar, href: "/admin/users" },
  ];

  let activeLinks = links;
  if (profile?.role === "TenantAdmin" || profile?.role === "TenantOwner") {
    activeLinks = [...links, ...tenantAdminLinks];
  }
  if (profile?.role === "GlobalAdmin") {
    activeLinks = [...links, ...tenantAdminLinks, ...globalAdminLinks];
  }

  return (
    <ScrollArea>
      <div style={{ padding: "var(--mantine-spacing-xs)" }}>
        {activeLinks.map((link) => (
          <NavLink
            key={link.href}
            href={link.href}
            label={link.label}
            leftSection={
              <ThemeIcon variant="light" size="sm">
                <link.icon size={16} />
              </ThemeIcon>
            }
            rightSection={link.rightSection}
            active={location.startsWith(link.href)}
            onClick={() => toggle?.()}
            style={{ borderRadius: "var(--mantine-radius-sm)", marginBottom: 2 }}
          />
        ))}
      </div>
    </ScrollArea>
  );
}

function CreditBadge() {
  const { t, i18n } = useTranslation("Veft");
  const tenantProfile = useStore((state) => state.tenantProfile);
  const remaining = tenantProfile?.creditsRemaining;
  if (remaining == null) return null;

  // Show the absolute balance, not a percentage: the percentage's denominator shifts every
  // time credits are bought, so it kept dropping and read as confusing. Colour still reflects
  // relative depletion when known (a coarse urgency cue), and goes red once the balance hits 0.
  const pctRemaining =
    tenantProfile.creditUsagePercent != null ? 100 - tenantProfile.creditUsagePercent : null;
  const color =
    remaining <= 0 ? "red"
      : pctRemaining == null ? "teal"
        : pctRemaining > 50 ? "teal"
          : pctRemaining > 20 ? "yellow"
            : "red";

  const formatted = remaining.toLocaleString(i18n.language);

  return (
    <Tooltip label={t("credits_remaining_tooltip", { credits: formatted })}>
      <Badge size="sm" radius="sm" color={color} variant="light" leftSection={<IconCreditCard size={12} />}>
        {formatted}
      </Badge>
    </Tooltip>
  );
}

function AppHeader({ opened, toggle, onOpenChat }) {
  const profile = useStore((state) => state.profile);
  const tenantProfile = useStore((state) => state.tenantProfile);
  const { canUseChat } = usePlan();
  const [, setLocation] = useLocation();
  const { t, i18n } = useTranslation();
  const { t: tv } = useTranslation("Veft");

  const creditsRemaining = tenantProfile?.creditsRemaining;

  return (
    // wrap="nowrap": on a phone the action cluster must never drop to a second row and spill
    // below the fixed 60px header. It stays on one row because the crowding children collapse
    // on mobile (LangSelector goes icon-only, the credit badge moves into the user Menu).
    <Group h="100%" px={{ base: "xs", sm: "md" }} justify="space-between" wrap="nowrap">
      <Group wrap="nowrap">
        <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
        <Wordmark />
      </Group>

      {profile && (
        <Group gap="xs" wrap="nowrap">
          <Tooltip label={canUseChat ? tv("assistant_trigger_tooltip") : tv("assistant_trigger_tooltip_locked")}>
            <ActionIcon
              variant="subtle"
              size="lg"
              onClick={() =>
                canUseChat
                  ? onOpenChat()
                  : requestUpgrade(tv("assistant_upgrade_prompt"))
              }
            >
              <IconMessageChatbot size={20} />
            </ActionIcon>
          </Tooltip>
          <LangSelector />
          <Box visibleFrom="sm"><CreditBadge /></Box>
          <NotificationBell />
          <Menu shadow="md" width={200}>
            <Menu.Target>
              <Button variant="subtle" size="sm" px="xs">
                <Group gap="xs">
                  <UserInitialsAvatar name={`${profile.firstName ?? ""} ${profile.lastName ?? ""}`} size="sm" />
                  <Text size="sm" visibleFrom="sm">{profile.firstName} {profile.lastName}</Text>
                </Group>
              </Button>
            </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>{profile.emailAddress}</Menu.Label>
            {/* The credit badge is hidden from the header on phones (it crowds the bar); surface the
                balance here instead so mobile users still see it and can top up in one tap. */}
            {creditsRemaining != null && (
              <Menu.Item
                hiddenFrom="sm"
                leftSection={<IconCreditCard size={14} />}
                onClick={() => setLocation("/credits")}
              >
                {tv("credits_remaining_tooltip", { credits: creditsRemaining.toLocaleString(i18n.language) })}
              </Menu.Item>
            )}
            <Menu.Item leftSection={<IconUser size={14} />} onClick={() => setLocation("/profile")}>
              {t("settings.profile", "Profile")}
            </Menu.Item>
            <Menu.Item leftSection={<IconAdjustments size={14} />} onClick={() => setLocation("/preferences")}>
              {tv("preferences_nav")}
            </Menu.Item>
            <Menu.Item leftSection={<IconKey size={14} />} onClick={() => setLocation("/auth/change-password")}>
              {t("settings.change_password", "Change password")}
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item leftSection={<IconLogout size={14} />} color="red" onClick={() => userLogout()}>
              {t("generic.logout", "Log out")}
            </Menu.Item>
          </Menu.Dropdown>
          </Menu>
        </Group>
      )}
    </Group>
  );
}

// Root ("/") landing. Waits for entitlements (instant once cached in localStorage) so it
// doesn't bounce candidates→activity.
function HomeRedirect() {
  const { canUseCollaboration, loaded } = usePlan();
  const homePage = useStore((state) => state.homePage);
  const hideActivity = useStore((state) => state.hideActivity);
  if (!loaded) return null;
  return <Redirect to={resolveHomePath(homePage, canUseCollaboration && !hideActivity)} />;
}

function AppContent() {
  const [opened, { toggle }] = useDisclosure(false);
  const [chatOpen, setChatOpen] = useState(false);
  const profile = useStore((state) => state.profile);
  const setTenantProfile = useStore((state) => state.setTenantProfile);
  const setEntitlements = useStore((state) => state.setEntitlements);
  const mustEnrollPasskey = useStore((state) => state.mustEnrollPasskey);
  const isLoggedIn = !!profile?.role;
  const [matchConsent, consentParams] = useRoute("/consent/:token");
  const [matchConsentPreview, consentPreviewParams] = useRoute("/consent/preview/:token");

  // Refresh tenant profile + plan entitlements on mount so the credit badge and
  // plan-gating (usePlan) stay current.
  useEffect(() => {
    if (isLoggedIn) {
      getTenantProfile().then(setTenantProfile).catch(() => {});
      getEntitlements().then(setEntitlements).catch(() => {});
    }
  }, [isLoggedIn]);

  // Public consent microsite — standalone, full-page, outside the authenticated
  // app shell (no header/navbar). The data subject reaches it via the email token.
  // Preview is the self-test variant: same page, records nothing (checked first as
  // it's the more specific path).
  if (matchConsentPreview) {
    return <ConsentReview token={consentPreviewParams.token} preview />;
  }
  if (matchConsent) {
    return <ConsentReview token={consentParams.token} />;
  }

  return (
    <AppShell
      header={{ height: 60 }}
      // Sole mobile gutter (10px). Page Containers add none on phones (Container defaultProps
      // px base:0), so the shell + page paddings stop stacking to ~32px on a 390px screen.
      padding={{ base: "xs", sm: "md" }}
      navbar={{
        width: 220,
        breakpoint: "sm",
        collapsed: { mobile: !opened, desktop: !isLoggedIn },
      }}
    >
      <AppShell.Header>
        <ErrorBoundary fallback={ErrorFallback}>
          <AppHeader opened={opened} toggle={toggle} onOpenChat={() => setChatOpen(true)} />
        </ErrorBoundary>
      </AppShell.Header>
      {isLoggedIn && (
        <AppShell.Navbar p={0}>
          <ErrorBoundary fallback={ErrorFallback}>
            <AppNavbar toggle={toggle} />
          </ErrorBoundary>
        </AppShell.Navbar>
      )}
      <AppShell.Main>
        <ErrorBoundary fallback={ErrorFallback}>
        <Switch>
          {/* Auth routes — always accessible */}
          <Route path="/auth/login">
            <LoginForm realm="VeftUser" hideGoogle />
          </Route>
          <Route path="/auth/login/:token" component={LoginLinkHandler} />
          <Route path="/auth/register">
            <RegisterForm realm="VeftUser" hideGoogle />
          </Route>
          <Route path="/auth/reset">
            <ForgotPassword realm="VeftUser" />
          </Route>
          <Route path="/auth/reset-password/:token" component={ResetPassword} />
          <Route path="/auth/confirm-email/:token" component={Confirmation} />
          <Route path="/invite/:token">
            {(params) => <InvitationForm appName="Veft" params={params} hideGoogle />}
          </Route>

          {/* Everything below requires auth */}
          {!isLoggedIn && (
            <Route><Redirect to="/auth/login" /></Route>
          )}

          {/* Tenant mandates passkey sign-in and this user has none yet: gate the whole app
              behind passkey enrollment until they create one. */}
          {isLoggedIn && mustEnrollPasskey && (
            <Route><ForcedPasskeyEnrollment /></Route>
          )}

          {isLoggedIn && !mustEnrollPasskey && (
            <>
              {/* App routes */}
              <Route path="/candidates/import" component={ImportPage} />
              <Route path="/candidates/deleted" component={DeletedCandidatesPage} />
              <Route path="/candidates" component={CandidateList} />
              <Route path="/candidates/:id" component={CandidateProfile} />
              <Route path="/tasks" component={TasksPage} />
              <Route path="/projects" component={ProjectList} />
              <Route path="/projects/:id" component={ProjectPipeline} />
              <Route path="/clients" component={ClientList} />
              <Route path="/clients/:id" component={ClientDetail} />
              <Route path="/companies/:id" component={CompanyPage} />
              <Route path="/search" component={SearchPage} />
              <Route path="/documents" component={DocumentsPage} />
              <Route path="/tools" component={ToolsPage} />
              <Route path="/activity" component={ActivityFeed} />
              <Route path="/preferences" component={PreferencesPage} />
              <Route path="/plans" component={PlansPage} />
              <Route path="/credits" component={CreditsPage} />
              <Route path="/profile" component={ProfileEdit} />
              <Route path="/auth/change-password" component={ChangePassword} />
              <Route path="/auth/set-password" component={SetPassword} />

              {/* Tenant owner / admin routes */}
              {(profile?.role === "TenantOwner" || profile?.role === "TenantAdmin") && (
                <>
                  <Route path="/settings/tenant">
                    <TenantProfileEdit showApiKeys={false} />
                  </Route>
                  <Route path="/settings/tags" component={AdminTagsPage} />
                  <Route path="/settings/api-keys" component={AdminApiKeysPage} />
                  <Route path="/settings/enrichment" component={EnrichmentSettingsPage} />
                  <Route path="/settings/consent" component={ConsentSettingsPage} />
                  <Route path="/settings/export" component={DataExportPage} />
                  <Route path="/settings/spend-report" component={SpendReportPage} />
                  <Route path="/users" component={UserList} />
                  <Route path="/users/add">
                    <UserAdd realm="VeftUser" />
                  </Route>
                  <Route path="/users/details/:id" component={UserDetails} />
                  <Route path="/users/edit/:id" component={UserEdit} />
                </>
              )}

              {/* Global admin routes */}
              {profile?.role === "GlobalAdmin" && (
                <>
                  <Route path="/settings/tenant">
                    <TenantProfileEdit showApiKeys={false} />
                  </Route>
                  <Route path="/settings/tags" component={AdminTagsPage} />
                  <Route path="/settings/api-keys" component={AdminApiKeysPage} />
                  <Route path="/settings/enrichment" component={EnrichmentSettingsPage} />
                  <Route path="/settings/consent" component={ConsentSettingsPage} />
                  <Route path="/settings/spend-report" component={SpendReportPage} />
                  <Route path="/users" component={UserList} />
                  <Route path="/users/add">
                    <UserAdd realm="VeftUser" />
                  </Route>
                  <Route path="/users/details/:id" component={UserDetails} />
                  <Route path="/users/edit/:id" component={UserEdit} />
                  <Route path="/admin/jobs" component={JobListAdmin} />
                  <Route path="/admin/jobs/:id" component={JobDetailsAdmin} />
                  <Route path="/tenants" component={TenantList} />
                  <Route path="/tenants/create" component={TenantCreate} />
                  <Route path="/tenants/:id">
                    {(params) => (
                      // Veft tiers (VeftPlans): Trial → Pro → Enterprise. Anything else
                      // normalizes to Trial server-side, so don't offer Free/Starter/Growth.
                      <TenantEdit
                        params={params}
                        plans={["Trial", "Pro", "Enterprise"]}
                      />
                    )}
                  </Route>
                  <Route path="/tenants/:tenantId/users/create" component={TenantUserCreate} />
                  <Route path="/admin/users" component={AdminUsersList} />
                  <Route path="/admin/users/:userId" component={AdminUserDetails} />
                  <Route path="/admin/templates" component={AdminTemplatesPage} />
                  <Route path="/admin/companies" component={AdminCompaniesPage} />
                  <Route path="/admin" component={AdminDashboard} />
                </>
              )}

              {/* Root redirect — land on the team activity feed when the tenant has it, else candidates. */}
              <Route path="/"><HomeRedirect /></Route>
            </>
          )}
        </Switch>
        </ErrorBoundary>
      </AppShell.Main>
      {isLoggedIn && <ChatDrawer opened={chatOpen} onClose={() => setChatOpen(false)} />}
      {isLoggedIn && <UpgradeRequiredModal />}
    </AppShell>
  );
}

export default function App() {
  return (
    <Router>
      <NBoilerplateContext.Provider value={store}>
        <MantineProvider
          theme={shadcnTheme}
          cssVariablesResolver={shadcnCssVariableResolver}
          forceColorScheme="light"
        >
          <ModalsProvider>
            <Notifications />
            <AppContent />
          </ModalsProvider>
        </MantineProvider>
      </NBoilerplateContext.Provider>
    </Router>
  );
}
